import React, { useState, useEffect, useCallback } from "react";
import DOMPurify from "dompurify";
import {
  getExternalRecords,
  getExternalRecordById,
} from "../../api/apiService";
import { useToast } from "./ToastContainer";
import { sanitizeUrl } from "../../utils/inputValidation";

// Strict DOMPurify configuration to prevent XSS from external FHIR narrative payloads
const SANITIZE_CONFIG = {
  USE_PROFILES: { html: true },
  ALLOWED_TAGS: [
    "div",
    "span",
    "p",
    "b",
    "i",
    "em",
    "strong",
    "a",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    "ul",
    "ol",
    "li",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "br",
    "hr",
    "pre",
    "code",
  ],
  ALLOWED_ATTR: ["href", "target", "class", "style", "rel"],
  FORBID_TAGS: ["script", "iframe", "object", "embed", "link", "style"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
};

// Helper to get resource from bundle by reference (supports "Type/id" and "#Type/id")
const getResource = (bundle, reference) => {
  if (!bundle || !bundle.entry || !reference) return null;
  const refNorm = reference.startsWith("#") ? reference.slice(1) : reference;
  const parts = refNorm.split("/");
  const resourceType = parts[0];
  const id = parts[1];
  const entry = bundle.entry.find((e) => {
    const fullUrl = e.fullUrl || "";
    const fullUrlNorm = fullUrl.startsWith("#") ? fullUrl.slice(1) : fullUrl;
    if (fullUrl === reference || fullUrlNorm === refNorm) return true;
    if (e.resource?.id === id && e.resource?.resourceType === resourceType)
      return true;
    if (fullUrl.endsWith(refNorm) || fullUrlNorm.endsWith(refNorm)) return true;
    return false;
  });
  return entry?.resource;
};

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

const AbhaExternalRecordsModal = ({
  patientId,
  show,
  onClose,
  patientData,
}) => {
  console.log({ patientData, patientId });
  const { showError } = useToast();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [recordLoading, setRecordLoading] = useState(false);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    setRecords([]);
    try {
      // Fetch records for the patient (using patientId or ABHA address)
      // The API accepts either depending on backend logic, usually ABHA address for external
      const idToUse =
        patientData?.id || patientId || patientData?.formattedAbhaId;
      const response = await getExternalRecords(idToUse);

      if (response && response.data) {
        setRecords(response.data);
      } else if (Array.isArray(response)) {
        setRecords(response);
      }
    } catch (error) {
      console.error("Error fetching external records:", error);
      showError("Failed to fetch external records.");
    } finally {
      setLoading(false);
    }
  }, [patientId, patientData?.formattedAbhaId, showError]);

  useEffect(() => {
    if (show && patientId) {
      fetchRecords();
    }
  }, [show, patientId, fetchRecords]);

  const getRecordTitle = (record) => {
    const ctx = record?.careContextReference?.trim();
    const hip = record?.sourceHipName || "Unknown source";
    if (ctx) return `${ctx} - ${hip}`;
    return `Record from ${hip}`;
  };

  const getRecordDate = (record) => {
    const date = new Date(record.receivedAt);
    if (isNaN(date.getTime())) return record.receivedAt;

    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getRecordType = (record) => {
    if (record.hiTypes && record.hiTypes.length > 0) {
      // Map common codes to readable text if needed
      return record.hiTypes.map((t) => t.replace(/_/g, " ")).join(", ");
    }
    return "Health Record";
  };

  const handleRecordClick = async (record) => {
    try {
      setRecordLoading(true);
      setSelectedRecord(null); // Clear previous selection
      const fullRecord = await getExternalRecordById(record._id);
      if (fullRecord && fullRecord.data) {
        setSelectedRecord(fullRecord.data);
      } else {
        setSelectedRecord(record); // Fallback
      }
    } catch (error) {
      console.error("Error fetching record details:", error);
      showError("Failed to fetch full record details.");
    } finally {
      setRecordLoading(false);
    }
  };

  const renderFhirBundle = (record) => {
    if (recordLoading) {
      return (
        <div className="text-center p-4">
          <div
            className="spinner-border text-primary spinner-border-sm"
            role="status"
          ></div>
          <span className="ms-2">Loading full record...</span>
        </div>
      );
    }

    if (!record || !record.fhirBundle) return <p>No data available</p>;

    const bundle = record.fhirBundle;
    const composition = bundle.entry?.find(
      (e) => e.resource?.resourceType === "Composition",
    )?.resource;

    const resolveRefDisplay = (ref) => {
      if (!ref) return null;
      if (ref.display) return ref.display;
      const r = ref.reference && getResource(bundle, ref.reference);
      if (r?.resourceType === "Practitioner") {
        const n = r.name?.[0];
        if (n?.text) return n.text;
        const given = n?.given?.join?.(" ") || "";
        const family = n?.family || "";
        return (given + " " + family).trim() || null;
      }
      if (r?.resourceType === "Organization") return r.name || null;
      return null;
    };

    if (!composition) {
      return (
        <div className="alert alert-warning">
          Invalid FHIR Bundle: No Composition Resource found.
          <pre className="mt-2 text-muted small">
            {JSON.stringify(bundle, null, 2)}
          </pre>
        </div>
      );
    }

    const sections = composition.section || [];

    const custodianDisplay =
      composition.custodian?.display ||
      resolveRefDisplay(composition.custodian) ||
      record.sourceHipName ||
      "External Hospital";
    const authorDisplay =
      composition.author?.[0]?.display ||
      resolveRefDisplay(composition.author?.[0]) ||
      "Unknown Doctor";

    return (
      <div className="fhir-viewer">
        {/* Header */}
        <div className="card mb-3 border bg-light">
          <div className="card-body p-3">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
              <div className="flex-grow-1 min-w-0">
                <h6 className="card-title mb-2">
                  {composition.title || "Health Record"}
                </h6>
                <div className="text-muted small">
                  <strong>Hospital:</strong> {custodianDisplay}
                  <br />
                  <strong>Doctor:</strong> {authorDisplay}
                </div>
              </div>
              <div className="small text-muted text-nowrap">
                <strong>Date:</strong> {formatDate(composition.date)}
              </div>
            </div>
          </div>
        </div>

        {/* Sections */}
        {sections.map((section, idx) => (
          <div key={idx} className="mb-4 bg-light rounded p-2 pb-3">
            <h6 className="px-2 pt-2 mb-2 border-bottom pb-2 small text-uppercase text-secondary">
              {section.title || "Untitled Section"}
            </h6>
            <div className="px-2">
              {/* Section narrative only when no structured entries (avoids duplicate content) */}
              {section.text?.div &&
                (!section.entry || section.entry.length === 0) && (
                  <div
                    className="small border rounded p-2 mb-2 bg-white section-narrative overflow-auto fhir-narrative"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(
                        section.text.div,
                        SANITIZE_CONFIG,
                      ),
                    }}
                    style={{ maxWidth: "100%", wordBreak: "break-word" }}
                  />
                )}
              {section.entry && section.entry.length > 0 ? (
                section.entry.map((entryRef, entryIdx) => {
                  const resource = getResource(bundle, entryRef.reference);
                  if (!resource) return null;

                  return (
                    <div key={entryIdx} className="mb-2">
                      {renderResource(resource, bundle)}
                    </div>
                  );
                })
              ) : !section.text?.div ? (
                <p className="text-muted small mb-0">No data in this section</p>
              ) : null}
            </div>
          </div>
        ))}

        {/* Fallback for unlinked resources (Attachments often float) */}
        {/* Only show Binary/DocRef if not found in sections? */}
      </div>
    );
  };

  const renderResource = (resource, bundle) => {
    switch (resource.resourceType) {
      case "MedicationRequest": {
        const med =
          resource.medicationCodeableConcept?.text ||
          resource.medicationReference?.display ||
          "Unknown Drug";
        const dos = resource.dosageInstruction?.[0];
        const freq = dos?.timing?.repeat?.frequency
          ? `${dos.timing.repeat.frequency}x per ${dos.timing.repeat.period || "day"}`
          : dos?.timing?.code?.text || null;
        const durVal =
          typeof dos?.duration === "object"
            ? dos.duration?.value
            : dos?.duration;
        const durUnit =
          typeof dos?.duration === "object"
            ? dos.duration?.unit
            : dos?.durationUnit?.coding?.[0]?.code ||
              dos?.durationUnit ||
              "days";
        const duration = durVal != null ? `${durVal} ${durUnit}` : null;
        const instructions = dos?.patientInstruction || dos?.text || null;
        return (
          <div className="card shadow-sm border mb-2">
            <div className="card-body p-2 bg-light bg-opacity-25 rounded border">
              <div className="d-flex justify-content-between align-items-start gap-2">
                <div className="min-w-0 flex-grow-1">
                  <strong className="text-primary d-block">{med}</strong>
                  <div className="small text-muted mt-1">
                    {freq && (
                      <span className="d-block">
                        <strong>Frequency:</strong> {freq}
                      </span>
                    )}
                    {duration && (
                      <span className="d-block">
                        <strong>Duration:</strong> {duration}
                      </span>
                    )}
                    {instructions && (
                      <span className="d-block">
                        <strong>Instructions:</strong> {instructions}
                      </span>
                    )}
                    {!freq && !duration && !instructions && (
                      <span>{dos?.text || "As directed"}</span>
                    )}
                  </div>
                </div>
                {resource.status && (
                  <span className="badge bg-secondary align-self-start text-nowrap">
                    {resource.status}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      }

      case "Condition":
        const conditionText = resource.code?.text || "Condition";
        const displayCondition =
          conditionText.toLowerCase() === "sub"
            ? "Subjective Finding"
            : conditionText;

        return (
          <div className="py-2 px-2">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <strong>{displayCondition}</strong>
                {/* {resource.clinicalStatus && (
                  <span className="ms-2 badge bg-success text-white">
                    {resource.clinicalStatus.coding?.[0]?.code || "Active"}
                  </span>
                )} */}
              </div>
            </div>
            {resource.note && (
              <ul className="small text-muted mt-1">
                {resource.note.map((note, idx) => (
                  <li key={idx}>{note.text}</li>
                ))}
              </ul>
            )}
          </div>
        );

      case "Observation":
        // Vitals, symptoms, physical examination, or standalone observations
        let obsCode = resource.code?.text || "Observation";
        if (obsCode.toLowerCase() === "obj") obsCode = "Objective Finding";
        if (obsCode.toLowerCase() === "sub") obsCode = "Subjective Finding";

        const compVal = resource.component
          ?.map((c) =>
            `${c.code?.text || "Value"}: ${c.valueQuantity?.value ?? ""} ${c.valueQuantity?.unit ?? ""}`.trim(),
          )
          .filter(Boolean)
          .join(", ");
        const val =
          resource.valueQuantity?.value !== undefined
            ? `${resource.valueQuantity.value} ${resource.valueQuantity.unit || ""}`.trim()
            : resource.valueString || compVal || "";

        // Always show observation (symptoms, physical exam may have only code or note)
        return (
          <div className="d-flex justify-content-between border-bottom py-2 align-items-start">
            <span className="text-muted fw-medium py-1">{obsCode}</span>
            <span
              className="fw-bold text-dark text-end"
              style={{ maxWidth: "60%" }}
            >
              {val || "—"}
              {resource.note && (
                <div className="small fw-normal text-muted mt-1">
                  {resource.note[0]?.text}
                </div>
              )}
            </span>
          </div>
        );

      case "DiagnosticReport":
        return (
          <div className="card mb-3 border bg-white shadow-sm">
            <div className="card-header bg-transparent border-bottom-0 pb-0">
              <strong>{resource.code?.text || "Diagnostic Report"}</strong>
              <div className="small text-muted">
                {resource.effectiveDateTime
                  ? new Date(resource.effectiveDateTime).toLocaleString()
                  : resource.effectivePeriod?.start
                    ? new Date(resource.effectivePeriod.start).toLocaleString()
                    : ""}
              </div>
            </div>
            <div className="card-body pt-2">
              {/* Attachments */}
              {resource.presentedForm?.map((att, i) => (
                <div key={i} className="mb-2">
                  {att.data ? (
                    att.contentType === "application/pdf" ? (
                      <embed
                        src={`data:application/pdf;base64,${att.data}`}
                        width="100%"
                        height="300px"
                      />
                    ) : (
                      <img
                        src={`data:${att.contentType};base64,${att.data}`}
                        className="img-fluid rounded"
                        alt="Report"
                      />
                    )
                  ) : (
                    <a
                      href={sanitizeUrl(att.url)}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-sm btn-outline-primary"
                    >
                      View Attachment ({att.title || "Document"})
                    </a>
                  )}
                </div>
              ))}

              {/* Individual Results - table with Test, Result, Unit, Date, Analyst */}
              {resource.result?.length > 0 && (
                <div className="mt-2">
                  <h6 className="small text-uppercase text-muted border-bottom pb-1 mb-2">
                    Results
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0 small">
                      <thead className="table-light">
                        <tr>
                          <th>Test</th>
                          <th>Result</th>
                          <th>Unit</th>
                          <th>Date</th>
                          <th>Analyst</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resource.result.map((ref, idx) => {
                          const obs = getResource(bundle, ref.reference);
                          if (!obs) return null;
                          const testName = obs.code?.text || "—";
                          const resultVal =
                            obs.valueQuantity?.value ?? obs.valueString ?? "—";
                          const unit = obs.valueQuantity?.unit ?? "";
                          const dateVal = obs.effectiveDateTime
                            ? new Date(
                                obs.effectiveDateTime,
                              ).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })
                            : "—";
                          const analyst = obs.performer?.[0]?.display ?? "—";
                          return (
                            <tr key={idx}>
                              <td>{testName}</td>
                              <td>{resultVal}</td>
                              <td>{unit}</td>
                              <td>{dateVal}</td>
                              <td>{analyst}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case "Encounter":
        return (
          <></>
          // <div className="mb-2 p-2 bg-light rounded small text-muted border">
          /* <strong>Encounter:</strong> {resource.class?.code}
            {resource.type?.[0]?.text && ` - ${resource.type[0].text}`}
            {resource.period?.start &&
              ` (${new Date(resource.period.start).toLocaleDateString()})`}
            {resource.serviceProvider && (
              <div className="d-block text-truncate">
                Hospital: {resource.serviceProvider.display}
              </div>
            )} */
          /* </div> */
        );

      case "AllergyIntolerance":
        return (
          <div className="alert alert-warning py-2 px-2 mb-1">
            <strong>Allergy: </strong> {resource.code?.text || "Unknown"}
            {resource.reaction?.[0] && (
              <div className="small mt-1">
                Reaction:{" "}
                {resource.reaction[0].manifestation?.[0]?.text ||
                  "Not specified"}
              </div>
            )}
          </div>
        );

      case "Immunization": {
        const occDate = resource.occurrenceDateTime
          ? new Date(resource.occurrenceDateTime).toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
          : "Done";
        return (
          <div className="d-flex justify-content-between align-items-center border-bottom py-2 gap-2">
            <span className="fw-medium flex-grow-1 min-w-0">
              {resource.vaccineCode?.text || "Vaccination"}
            </span>
            <span className="text-muted text-nowrap">{occDate}</span>
          </div>
        );
      }

      case "Procedure":
        return (
          <div className="mb-2 p-2 border rounded">
            <strong>{resource.code?.text || "Procedure"}</strong>
            <div className="small text-muted">
              {resource.performedDateTime
                ? new Date(resource.performedDateTime).toLocaleDateString()
                : ""}
            </div>
          </div>
        );

      case "Invoice":
        const invoiceDate = resource.date
          ? new Date(resource.date).toLocaleDateString()
          : "";
        const totalNet = resource.totalNet?.value ?? resource.totalGross?.value;
        const totalCurr =
          resource.totalNet?.currency || resource.totalGross?.currency || "INR";
        return (
          <div className="card mb-3 border bg-white shadow-sm">
            <div className="card-header bg-light d-flex justify-content-between align-items-center flex-wrap gap-2 py-2 px-3">
              <strong>Invoice</strong>
              <span className="badge bg-secondary">
                {resource.status || "unknown"}
              </span>
            </div>
            <div className="card-body p-3">
              {invoiceDate && (
                <div className="small text-muted mb-2">Date: {invoiceDate}</div>
              )}
              {resource.type?.text && (
                <div className="small mb-2">{resource.type.text}</div>
              )}
              {resource.lineItem && resource.lineItem.length > 0 && (
                <div className="mb-2">
                  <h6 className="small text-uppercase text-muted border-bottom pb-1 mb-2">
                    Line items
                  </h6>
                  <ul className="list-unstyled small mb-0">
                    {resource.lineItem.map((item, i) => {
                      const chargeText =
                        item.chargeItemCodeableConcept?.text ||
                        item.chargeItemReference?.display ||
                        `Item ${item.sequence || i + 1}`;
                      const priceComp = item.priceComponent?.[0];
                      const amt = priceComp?.amount?.value ?? priceComp?.amount;
                      const curr = priceComp?.amount?.currency || "";
                      return (
                        <li
                          key={i}
                          className="d-flex justify-content-between py-1 border-bottom border-light"
                        >
                          <span>{chargeText}</span>
                          {amt != null && (
                            <span className="text-dark">
                              {curr ? `${curr} ` : ""}
                              {amt}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {totalNet != null && (
                <div className="d-flex justify-content-between fw-bold mt-2 pt-2 border-top">
                  <span>Total</span>
                  <span>
                    {totalCurr} {totalNet}
                  </span>
                </div>
              )}
            </div>
          </div>
        );

      case "Binary":
        return (
          <div className="mt-2">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <p className="mb-0">
                <strong>Document Attachment:</strong>
              </p>
              {resource.contentType === "application/pdf" && (
                <a
                  download="document.pdf"
                  href={`data:application/pdf;base64,${resource.data}`}
                  className="btn btn-sm btn-outline-secondary"
                >
                  ⬇ Download
                </a>
              )}
            </div>

            {resource.contentType === "application/pdf" && (
              <embed
                src={`data:application/pdf;base64,${resource.data}`}
                width="100%"
                height="400px"
                type="application/pdf"
              />
            )}
            {resource.contentType?.startsWith("image/") && (
              <img
                src={`data:${resource.contentType};base64,${resource.data}`}
                alt="Attachment"
                style={{ maxWidth: "100%" }}
              />
            )}
          </div>
        );

      case "CarePlan":
        return (
          <div className="card mb-2 ">
            <div className="card-header bg-opacity-10 py-1">
              <strong>Follow-up / Care Plan</strong>
            </div>
            <div className="card-body p-2">
              {resource.title && (
                <div>
                  <strong>{resource.title}</strong>
                </div>
              )}
              {resource.description && (
                <div className="small">{resource.description}</div>
              )}
              {resource.activity && (
                <ul className="mb-0 small ps-3">
                  {resource.activity.map((act, i) => (
                    <li key={i}>
                      {act.detail?.code?.text ||
                        act.detail?.description ||
                        "Activity"}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        );

      case "Appointment":
        return (
          <div className="alert alert-primary py-2 px-3 mb-2">
            <strong>Appointment: </strong>{" "}
            {resource.description || resource.serviceCategory?.[0]?.text} <br />
            <small>
              Date:{" "}
              {resource.start
                ? new Date(resource.start).toLocaleString()
                : "TBD"}
            </small>
          </div>
        );

      case "DocumentReference":
        const attachment = resource.content?.[0]?.attachment;
        if (attachment) {
          let contentData = attachment.data;
          let contentType = attachment.contentType;

          // Resolve internal Binary reference if data is missing
          if (
            !contentData &&
            attachment.url &&
            attachment.url.startsWith("Binary/")
          ) {
            const binaryId = attachment.url.split("/")[1];
            const binaryResource = bundle.entry?.find(
              (e) => e.resource?.id === binaryId,
            )?.resource;
            if (binaryResource) {
              contentData = binaryResource.data;
              contentType = binaryResource.contentType;
            }
          }

          if (contentData) {
            return (
              <div className="mt-2 p-2 border rounded bg-white">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0 text-primary">
                    {resource.description || resource.type?.text || "Document"}
                  </h6>
                  {(contentType === "application/pdf" ||
                    contentType === "application/json") && (
                    <a
                      download={`document.${contentType === "application/pdf" ? "pdf" : "json"}`}
                      href={`data:${contentType};base64,${contentData}`}
                      className="btn btn-sm btn-outline-primary"
                    >
                      ⬇ Download
                    </a>
                  )}
                </div>

                {contentType === "application/pdf" ? (
                  <embed
                    src={`data:application/pdf;base64,${contentData}`}
                    width="100%"
                    height="500px"
                    type="application/pdf"
                    className="border rounded"
                  />
                ) : contentType?.startsWith("image/") ? (
                  <img
                    src={`data:${contentType};base64,${contentData}`}
                    className="img-fluid rounded"
                    alt="Document"
                  />
                ) : (
                  <div className="alert alert-secondary small">
                    Attachment Type: {contentType} (Preview not supported)
                  </div>
                )}
              </div>
            );
          }
        }

        return (
          <div className="text-muted small border p-2 rounded">
            <strong>Document: </strong> {resource.type?.text || "Unknown"}{" "}
            (Content unavailable)
          </div>
        );

      case "Patient":
      case "Composition":
      case "Organization":
      case "Practitioner":
        return null;

      default:
        // Try to show narrative text if available (scoped so tables don't overlap)
        if (resource.text?.div) {
          return (
            <div
              className="small border p-2 mb-1 rounded bg-light fhir-narrative overflow-auto"
              style={{ maxWidth: "100%" }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(resource.text.div, SANITIZE_CONFIG),
              }}
            />
          );
        }
        return (
          <div className="text-muted small border rounded p-1">
            {resource.resourceType}: {JSON.stringify(resource).slice(0, 50)}...
          </div>
        );
    }
  };

  // ... existing code ...

  if (!show) return null;

  return (
    <div
      className="offcanvas offcanvas-end show"
      tabIndex="-1"
      style={{ visibility: "visible", width: "600px" }}
      aria-modal="true"
      role="dialog"
    >
      <style>{`
        .fhir-narrative table { width: 100%; table-layout: fixed; border-collapse: collapse; }
        .fhir-narrative td, .fhir-narrative th { padding: 4px 8px; border: 1px solid #dee2e6; word-break: break-word; }
      `}</style>
      <div className="offcanvas-header bg-light">
        <h5 className="offcanvas-title">ABHA Records</h5>
        <button
          type="button"
          className="btn-close text-reset"
          onClick={onClose}
          aria-label="Close"
        ></button>
      </div>
      <div className="offcanvas-body">
        {patientData && (
          <div className="mb-4 p-3 bg-white border rounded shadow-sm">
            <h6 className="m-0 text-primary">
              {patientData.fullName || "Patient"}
            </h6>
            <small
              className="text-muted d-block mt-1"
              style={{ fontSize: "12px", wordBreak: "break-all" }}
            >
              ABHA:{" "}
              {patientData.ABHAAddress ||
                patientData.abhaAddress ||
                patientData.abhaaddress ||
                patientData.formattedAbhaId ||
                "N/A"}
            </small>
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">
              Fetching records from ABDM network...
            </p>
          </div>
        ) : records.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <p>No external records found for this patient.</p>
            <small>
              Ensure consent has been granted and data fetch initiated.
            </small>
          </div>
        ) : (
          <div className="records-list">
            {selectedRecord || recordLoading ? (
              <div className="record-detail">
                <button
                  className="btn btn-sm btn-outline-secondary mb-3"
                  onClick={() => {
                    setSelectedRecord(null);
                    setRecordLoading(false);
                  }}
                >
                  ← Back to List
                </button>
                <div className="card border shadow-sm">
                  <div className="card-header bg-light py-2 px-3">
                    <strong className="d-block text-break">
                      {selectedRecord
                        ? getRecordTitle(selectedRecord)
                        : "Loading..."}
                    </strong>
                    {selectedRecord && (
                      <small
                        className="text-muted d-block mt-1"
                        style={{ fontSize: "12px" }}
                      >
                        {getRecordType(selectedRecord)} ·{" "}
                        {getRecordDate(selectedRecord)}
                      </small>
                    )}
                  </div>
                  <div className="card-body p-3">
                    {renderFhirBundle(selectedRecord)}
                  </div>
                </div>
              </div>
            ) : (
              <div className="list-group list-group-flush">
                {records.map((record) => (
                  <button
                    key={record._id}
                    type="button"
                    className="list-group-item list-group-item-action text-start py-3"
                    onClick={() => handleRecordClick(record)}
                  >
                    <div className="d-flex w-100 justify-content-between align-items-start gap-2">
                      <h6 className="mb-1 flex-grow-1 text-break">
                        {getRecordTitle(record)}
                      </h6>
                      <small
                        className="text-muted text-nowrap"
                        style={{ fontSize: "12px" }}
                      >
                        {getRecordDate(record)}
                      </small>
                    </div>
                    <p className="mb-1 small text-muted">
                      {getRecordType(record)}
                    </p>
                    <small className="text-muted">
                      Source: {record.sourceHipName || "Unknown Hospital"}
                    </small>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AbhaExternalRecordsModal;
